import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import {fetchWithAuth} from '../../libs/apiUtils'
import Modal from '../Modal'
import {Option} from '../../types/global'

interface User {
  id?: string
  fullName?: string
  email?: string
  role?: string
}

interface EditUserModalProps {
  user: User
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  roleOptions: Option[]
  onSuccess: () => void
}

interface EditUserRequest {
  id: string
  roleId: string
  fullName: string
  email: string
}

export default function EditUserModal({ 
  user,
  isOpen, 
  setIsOpen, 
  roleOptions, 
  onSuccess 
}: EditUserModalProps) {
  const [request, setRequest] = useState<EditUserRequest>({
    id: '',
    roleId: '',
    fullName: '',
    email: ''
  })
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!request.fullName.trim()) return toast.error('Nama lengkap tidak boleh kosong')
    if (!request.email.trim()) return toast.error('Email tidak boleh kosong')
    if (!request.roleId.trim()) return toast.error('Pilih role user')
    if (!user.id?.trim()) return toast.error('Tidak ada user yang dipilih')

    try {
      setLoading(true)
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/user-management`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        toast.error(data[0].message || 'Error saat memperbarui user')
        return
      }

      toast.success('User berhasil diperbarui')
      setIsOpen(false)
      onSuccess()
    } catch (err) {
      toast.error('Kesalahan jaringan, periksa koneksi Anda')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (user && roleOptions.length > 0) {
      setRequest({
          id: user.id || '',
          roleId: roleOptions.find((item) => item.value === user.role)?.id || '',
          fullName: user.fullName || '',
          email: user.email || ''
      })
    }
  }, [user, roleOptions])

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <h3 className="text-xl font-bold text-neutral-800 mb-4">Edit User</h3>
      <form className='flex flex-col gap-4' onSubmit={handleSubmit}>
        <div>
          <label htmlFor="fullName" className="block text-sm font-semibold text-neutral-600 mb-2">
            Nama Lengkap
          </label>
          <input
            type="text"
            id="fullName"
            className="border border-neutral-300 px-4 py-2 rounded-lg w-full focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all outline-none"
            value={request.fullName}
            onChange={(e) => setRequest((prev) => ({...prev, fullName: e.target.value}))}
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-neutral-600 mb-2">
            Alamat Email
          </label>
          <input
            type="email"
            id="email"
            className="border border-neutral-300 px-4 py-2 rounded-lg w-full focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all outline-none"
            value={request.email}
            onChange={(e) => setRequest((prev) => ({...prev, email: e.target.value}))}
          />
        </div>

        <div>
          <label htmlFor="roleId" className="block text-sm font-semibold text-neutral-600 mb-2">
            Role
          </label>
          <select
            id="roleId"
            className="border border-neutral-300 px-4 py-2 rounded-lg w-full focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all outline-none bg-white"
            value={request.roleId}
            onChange={(e) => setRequest((prev) => ({...prev, roleId: e.target.value}))}
          >
            <option value="">Pilih role</option>
            {roleOptions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.value}
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-end gap-3 mt-4">
          <button
            type="button"
            className="px-4 py-2 text-neutral-600 font-medium rounded-lg hover:bg-neutral-100 transition-colors"
            onClick={() => setIsOpen(false)}
            disabled={loading}
          >
            Batal
          </button>
          <button
            type="submit"
            className="bg-primary-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-primary-700 active:scale-95 disabled:opacity-50 disabled:active:scale-100 transition-all"
            disabled={loading}
          >
            {loading ? 'Menyimpan...' : 'Simpan Perubahan'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
